import React, { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import { CloseHeaderDropdownContext } from 'components/layout/header/closeHeaderDropdownContext';
import {
  COMPETITION_ENABLED,
  MARKET_MAKING_REWARDS_ENABLED,
  REFERRALS_ENABLED,
  POINTS_ENABLED,
  MILESTONE_COMPETITION_ENABLED,
} from 'config/constants';
import { redirectTo } from 'lib/redirect';

import { TESTNET_SANDBOX_COMPETITION_ENABLED } from './testnetSandbox/constants';

const Items = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  padding: 4px;

  .isDesktopStyle & {
    padding: 12px;
    background: radial-gradient(
      100px 100px at 0% 0%,
      var(--main) 0%,
      rgba(224, 62, 23, 0.01) 100px
    );
  }
`;

const UnicodeIcon = styled.span`
  font-size: 24px;
`;

const LinkBox = styled.div<{ disabled?: boolean }>`
  position: relative;
  padding: 16px 50px 16px 28px;
  min-height: 40px;
  display: flex;
  align-items: center;
  ${({ disabled }) =>
    disabled ? 'opacity: 0.8; filter: grayscale(100%);' : ''}
  border-radius: 4px;

  .isMobileStyle & {
    padding: 32px 50px 32px 28px;
    border-radius: 2px;
    background: var(--elevation-light);
  }

  &:hover {
    background: #02163b55;
    color: #fff;
    cursor: pointer;
  }
`;

const TextWrap = styled.div`
  padding-left: 16px;
  padding-right: 24px;
  .isMobileStyle & {
    padding-right: 4px;
  }
`;

const Heading = styled.h2`
  line-height: 1;
  display: inline-block;
  font-size: var(--medium);
  color: var(--white);
  padding: 0;
  margin: 0;
  .isMobileStyle & {
    font-size: var(--large);
    padding-bottom: 8px;
  }
`;

const Description = styled.div`
  font-size: var(--small);
  color: var(--gray4c);
`;

const ArrowWrap = styled.div`
  position: absolute;
  right: 14px;
  top: calc(50% - 15px);
`;

const ComingSoon = styled.div`
  position: absolute;
  right: 9px;
  top: 9px;
  border-radius: 4px;
  background: var(--black1b);
  padding: 3px 8px 4px 8px;

  color: var(--gray4c);
  font-size: var(--small);
  font-style: normal;
  font-weight: normal;
  line-height: normal;
  text-transform: uppercase;
`;

function RewardItem({
  icon,
  title,
  children,
  path,
  disabled,
}: {
  icon: string;
  title: string;
  children: ReactNode;
  path: string;
  disabled?: boolean;
}) {
  const closeDropdown = useContext(CloseHeaderDropdownContext);
  return (
    <LinkBox
      onClick={
        disabled
          ? undefined
          : () => {
              closeDropdown();
              redirectTo(path);
            }
      }
      disabled={disabled}
    >
      {disabled && <ComingSoon>Coming Soon</ComingSoon>}
      <div>
        <UnicodeIcon>{icon}</UnicodeIcon>
      </div>
      <TextWrap>
        <Heading>{title}</Heading>
        <Description>{children}</Description>
      </TextWrap>
      {disabled ? null : (
        <ArrowWrap>
          <MaterialSymbol
            icon="arrow_right"
            fontSize="large"
            color="var(--white)"
            rounded
          />
        </ArrowWrap>
      )}
    </LinkBox>
  );
}

export default function RewardsMenuSection() {
  return (
    <Items>
      {TESTNET_SANDBOX_COMPETITION_ENABLED || COMPETITION_ENABLED ? (
        <RewardItem
          title="Trading Competition"
          icon="⚡"
          path={
            TESTNET_SANDBOX_COMPETITION_ENABLED
              ? '/rewards/competition-leaderboard'
              : '/rewards/competition'
          }
        >
          Earn rewards just by trading.
        </RewardItem>
      ) : MILESTONE_COMPETITION_ENABLED ? (
        <RewardItem
          title="Trading Competition"
          icon="⚡"
          path="/rewards/competition"
        >
          Earn rewards just by trading.
        </RewardItem>
      ) : null}

      {POINTS_ENABLED && (
        <RewardItem
          title="Points"
          icon="💸"
          // disabled={!POINTS_ENABLED}
          path="/rewards/points"
        >
          Earn points with your activity.
        </RewardItem>
      )}

      {MARKET_MAKING_REWARDS_ENABLED && (
        <RewardItem
          title="Market Making"
          icon="🤝"
          path="/rewards/market-maker"
        >
          Enjoy exclusive rewards based on liquidity.
        </RewardItem>
      )}

      <RewardItem
        title="Referrals"
        icon="📢"
        path="/rewards/referrals"
        disabled={!REFERRALS_ENABLED}
      >
        Refer and earn affiliate fees.
      </RewardItem>
    </Items>
  );
}
